import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Text } from "@otrium/atoms";
import { Box, Flex, useBreakpoints } from "@otrium/core";
import Link from "next/link";
import { Locale } from "src/lib/Intl";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { formatName } from "src/utils/formatName";
import { composeBrandsByFirstLetter } from "../sortBrands";
import { ToggleWishListBrandButton } from "./ToggleWishListBrandButton";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import {
  Wrapper,
  LetterListWrapper,
  LetterList,
  LetterItem,
  RowContent,
  MainLetter,
  Row,
  ItemContent,
  Item,
} from "./SearchDesktopView.styled";
import { Content } from "src/atoms/Content";
import { Sticky } from "src/atoms/Sticky";
import type { AllBrandData } from "src/types/adapters/browse.type";

const StickyLetterList = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { isDesktop } = useBreakpoints();
  const initialState = useMemo(() => ({ container: {}, inner: {} }), []);
  const [styles, setStyles] = useState(initialState);
  const onChangeMode = useCallback(
    (mode: any) => {
      if (mode) {
        setStyles({
          inner: {
            maxWidth: "100%",
          },
          container: {
            background: "white",
          },
        });
      } else {
        setStyles(initialState);
      }
    },
    [setStyles, initialState]
  );

  const { container: containerStyles, inner: innerStyles } = styles;
  const contentRef = useRef<HTMLDivElement>(null);
  const [offsetTop, setOffsetTop] = useState<number | null>(null);
  useEffect(() => {
    const offset = contentRef?.current?.getBoundingClientRect()?.top;
    if (offset) {
      setOffsetTop(offset);
    }
  }, [contentRef, offsetTop, setOffsetTop]);

  const [scrollPosition, setScrollPosition] = useState(0);
  const handleScroll = () => {
    const position = window.pageYOffset;
    setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const isSticky =
    isDesktop &&
    offsetTop !== null &&
    offsetTop > 0 &&
    scrollPosition >= offsetTop;

  if (isSticky) {
    return (
      <Box display={["none", "none", "block", "block"]}>
        <Sticky
          offsetTop={-offsetTop + 185}
          onChange={onChangeMode}
          top={63}
          boxshadowMode={"down"}
        >
          <Box sx={{ ...containerStyles }}>
            <Box sx={{ ...innerStyles }}>{children}</Box>
          </Box>
        </Sticky>
      </Box>
    );
  }

  return <div ref={contentRef}>{children}</div>;
};

type SearchDesktopViewProps = {
  brands: AllBrandData[];
  genderId?: string;
  locale: Locale;
};

function SearchDesktopView({
  brands,
  genderId,
  locale,
}: SearchDesktopViewProps) {
  const isLoggedIn = useIsLoggedIn();
  const sortedBrands = composeBrandsByFirstLetter(brands);

  if (!sortedBrands) {
    return null;
  }

  return (
    <Wrapper>
      <LetterListWrapper>
        <StickyLetterList>
          <LetterList data-testid="search-letters-list-all-brands" as="ul">
            {Object.keys(sortedBrands).map((letter) => (
              <li key={letter}>
                <LetterItem
                  data-testid={`search-letter-${letter}`}
                  aria-disabled={!sortedBrands[letter].length}
                  onClick={() => {
                    document
                      ?.getElementById(`search-brand-letter-${letter}`)
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "center",
                      });
                  }}
                >
                  {letter}
                </LetterItem>
              </li>
            ))}
          </LetterList>
        </StickyLetterList>
      </LetterListWrapper>
      <Content data-testid="search-results-all-brands">
        {Object.keys(sortedBrands).map((letter) => {
          const letterBrands = sortedBrands[letter];
          if (!letterBrands.length) return null;

          return (
            <RowContent key={letter}>
              <MainLetter id={`search-brand-letter-${letter}`}>
                {letter}
              </MainLetter>
              <Row>
                {letterBrands.map((brand) => (
                  <Link
                    key={brand.id}
                    href={
                      genderId
                        ? `${getI18nRoute({
                            route: `/brands`,
                            locale,
                          })}/${brand.slug || brand.value}${getI18nRoute({
                            route: `/${genderId}`,
                            locale,
                          })}`
                        : `${getI18nRoute({
                            route: `/brands`,
                            locale,
                          })}/${brand.slug || brand.value}`
                    }
                    prefetch={false}
                  >
                    <ItemContent>
                      <Item>
                        <Flex>
                          {isLoggedIn ? (
                            <Flex
                              ml={5}
                              pl={2}
                              sx={{
                                position: "relative",
                                height: "100%",
                                top: "0",
                                marginTop: "-14px",
                              }}
                            >
                              <ToggleWishListBrandButton
                                brandId={brand.id}
                                brandName={brand.name}
                              />
                            </Flex>
                          ) : null}
                          <Flex>
                            <Text
                              as="p"
                              sx={{
                                wordBreak: "normal",
                              }}
                            >
                              {formatName(brand.name)}
                            </Text>
                          </Flex>
                        </Flex>
                      </Item>
                    </ItemContent>
                  </Link>
                ))}
              </Row>
            </RowContent>
          );
        })}
      </Content>
    </Wrapper>
  );
}

export default SearchDesktopView;
